/**
 * @fileoverview Import/export tree construction utilities.
 * Provides functions to build hierarchical tree structures for visualizing
 * and managing import/export changes in bot configurations.
 * @module helperMethods/import_export/util
 */

import _ from "lodash";

/**
 * Represents a leaf node in the changes tree (an actual data item).
 */
interface ChangesTreeLeaf {
  label: string;
  type: string;
  oldData: any;
  newData: any;
  overwrite?: boolean;
  new?: boolean;
  modified?: boolean;
}

/**
 * Represents a branch node in the changes tree (contains children).
 */
interface ChangesTreeBranch {
  label: string;
  type: string;
  modified?: boolean;
  children: (ChangesTreeLeaf | ChangesTreeLeaf)[];
}

type ChangesTreeLeafOrBranch = ChangesTreeLeaf | ChangesTreeBranch;

interface GenericData {
  data: object;
  changes: ChangesTreeLeaf;
}
interface GenericDataObject {
  [identifier: string]: GenericData;
}

/**
 * Checks if any nodes in the array have modification flags.
 * Looks for modified, new, or overwrite flags in leaf nodes,
 * or modified flag in branch nodes.
 *
 * @private
 * @param {Array<ChangesTreeLeaf | ChangesTreeBranch>} nodes - Array of tree nodes to check
 * @returns {boolean} True if any node has modifications
 */
function checkForModificationsInNodes(nodes: (ChangesTreeLeaf | ChangesTreeBranch)[]) {
  return !!_.find(nodes, (obj) => {
    if (instanceOfChangesTreeLeaf(obj)) {
      return obj.new || obj.modified;
    } else {
      return obj.modified;
    }
  });
}

/**
 * Type guard to determine if a tree node is a leaf or branch.
 * A node without a 'children' property is considered a leaf.
 *
 * @private
 * @param {ChangesTreeLeaf | ChangesTreeBranch} leafOrBranch - Node to check
 * @returns {boolean} True if the node is a leaf (no children property)
 */
function instanceOfChangesTreeLeaf(
  leafOrBranch: ChangesTreeLeaf | ChangesTreeBranch
): leafOrBranch is ChangesTreeLeaf {
  return !("children" in leafOrBranch);
}

/**
 * Constructs a hierarchical tree structure from generic data for import/export visualization.
 * Builds a tree with configurable depth levels and node types.
 *
 * @param {GenericDataObject} genericData - Data object containing items to be organized in tree
 * @param {string} label - Label for the root node
 * @param {number} maxLevel - Maximum depth of the tree hierarchy
 * @param {string} type - Type identifier for data objects (e.g., 'content', 'trigger')
 * @param {Function} prefixDelimiter - Function to group data by prefix at each level
 * @param {boolean} isImport - Whether this tree is for import (affects disabled state)
 * @returns {Object} Tree root object with label and children
 *
 * @example
 * const tree = constructTree(
 *   contentData,
 *   'Content Nodes',
 *   3,
 *   'content',
 *   (data, level, max) => groupByPrefix(data, level),
 *   true
 * );
 * // Returns: { label: 'Content Nodes', children: [...] }
 */
export function constructTree(
  genericData: GenericDataObject,
  label: string,
  maxLevel: number,
  type: string,
  prefixDelimiter: (
    genericData: GenericDataObject,
    currentLevel: number,
    maxLevel: number
  ) => { [identifier: string]: GenericDataObject },
  isImport: boolean
) {
  const children = constructTreeChildren(genericData, 1, maxLevel, type, prefixDelimiter, isImport);
  const childrenContainsAnyModification = checkForModificationsInNodes(children);

  // If children has no changes for imports, disable.
  if (!childrenContainsAnyModification && isImport) {
    return {
      label,
      children,
      disabled: true,
    };
  }
  return {
    label,
    children,
  };
}

/**
 * Recursively constructs children nodes for the tree structure.
 * Handles both leaf nodes (actual data items) and branch nodes (grouping categories).
 *
 * @private
 * @param {GenericDataObject} genericData - Data to construct children from
 * @param {number} currentLevel - Current depth level in the tree
 * @param {number} maxLevel - Maximum depth level
 * @param {string} type - Type identifier for nodes
 * @param {Function} prefixDelimiter - Function to group data by prefix
 * @param {boolean} isImport - Whether this is for import operation
 * @returns {Array<ChangesTreeLeafOrBranch>} Array of child nodes
 */
function constructTreeChildren(
  genericData: GenericDataObject,
  currentLevel: number,
  maxLevel: number,
  type: string,
  prefixDelimiter: (
    genericData: GenericDataObject,
    currentLevel: number,
    maxLevel: number
  ) => { [identifier: string]: GenericDataObject },
  isImport: boolean
): ChangesTreeLeafOrBranch[] {
  const contentPrefixes = prefixDelimiter(genericData, currentLevel, maxLevel);
  const keys = _.keys(contentPrefixes);
  if (currentLevel >= maxLevel) {
    const dataFilteredByPrefix = prefixDelimiter(genericData, currentLevel, maxLevel);
    const result = _.map(dataFilteredByPrefix, (data, prefix): ChangesTreeLeaf[] => {
      return _.map(data, (genericObj, identifier): ChangesTreeLeaf => {
        const changeInfo = _.get(genericObj, "changes", {});
        const data = _.get(genericObj, "data", {});
        const leaf = {
          label: "",
          type: "",
          oldData: {},
          newData: {},
        };
        const importDoesNotContainChange = _.isEmpty(changeInfo);

        let leafNodeWithChangeInfo = Object.assign({}, leaf, {
          label: identifier,
          type,
          ...changeInfo,
          ...data,
        });

        if (leafNodeWithChangeInfo.type == "object") {
          leafNodeWithChangeInfo = Object.assign(leafNodeWithChangeInfo, { type });
        }

        if (importDoesNotContainChange && isImport) {
          return Object.assign({}, leafNodeWithChangeInfo, {
            disabled: true,
          });
        }

        return leafNodeWithChangeInfo;
      });
    });
    return _.flattenDeep(result);
  } else if (keys.length === 1) {
    const onlyKey = keys[0];
    const changeInfo = _.get(genericData, `${onlyKey}.changes`, {});
    const data = _.get(genericData, `${onlyKey}.data`, {});
    const leaf = {
      label: "",
      type: "",
      oldData: {},
      newData: {},
    };
    const importDoesNotContainChange = _.isEmpty(changeInfo);

    const leafNodeWithChangeInfo = Object.assign({}, leaf, {
      label: onlyKey,
      type,
      ...changeInfo,
      ...data,
    });

    if (importDoesNotContainChange && isImport) {
      return [Object.assign({}, leafNodeWithChangeInfo, { disabled: true })];
    }

    return [leafNodeWithChangeInfo];
  } else {
    const contentPrefixes = prefixDelimiter(genericData, currentLevel, maxLevel);
    return _.flattenDeep(
      _.map(contentPrefixes, (data, identifier): ChangesTreeLeafOrBranch => {
        const children = constructTreeChildren(
          data,
          currentLevel + 1,
          maxLevel,
          type,
          prefixDelimiter,
          isImport
        );
        const childrenContainsAnyModification = checkForModificationsInNodes(children);
        const branch = {
          label: "",
          type: "",
          modified: false,
          children: [],
        };
        const branchWithChildren = Object.assign(branch, {
          label: identifier,
          children,
          modified: childrenContainsAnyModification,
        });

        // If children has no changes for imports, disable.
        if (!childrenContainsAnyModification && isImport) {
          return Object.assign({}, branchWithChildren, { disabled: true });
        }
        return branchWithChildren;
      })
    );
  }
}
